import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CreditCard, RefreshCw, RefreshCcw, Loader2, Save, RotateCcw, Trash2, Plus,
  ChevronDown, ChevronRight, CheckCircle2, XCircle, AlertTriangle, Package,
  Layers, Percent, Settings, Receipt, History, Wrench, Gift, Info, X, Activity,
} from 'lucide-react';
import { billingApi, formatPaise } from '../NextGenBuilder/api/billingApi';

/**
 * NexusBillingAdmin — the single admin console for TSG Nexus feature-based
 * billing (/superadmin/nexus-billing).
 *
 * Everything on this screen is rendered from `billingApi.admin.getConfig()`:
 * features and their free quotas, sub-features, products, Elite pricing
 * tiers, tax rates, settings, live metric counters, orders and the config
 * audit log. There are no hardcoded prices, quotas, feature codes, product
 * codes or technology slugs here — if a row exists in the database it shows
 * up, and if it doesn't, it doesn't. The server is the only authority: this
 * screen sends PATCHes with whitelisted columns (`editable[table].cols` from
 * the config payload) and re-reads the config after every write, so what you
 * see is always what the backend actually stored.
 *
 * Auth follows the rest of the admin console: the `user-id` header of a
 * users.role='admin' account, sent by `billingApi.admin.*`.
 */

const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.userId || user?.user_id || localStorage.getItem('user_id') || null;
  } catch {
    return localStorage.getItem('user_id') || null;
  }
};

/* Table identifiers used by PATCH /admin/config/:table/:key */
const T = {
  FEATURES: 'billing_features',
  SUB_FEATURES: 'billing_sub_features',
  PRODUCTS: 'billing_products',
  TIERS: 'billing_pricing_tiers',
  TAX: 'billing_tax_rates',
  SETTINGS: 'billing_settings',
};

/* Column enums (schema-level, not business values — the values themselves
   all come from the config payload). */
const QUOTA_SCOPE_OPTIONS = [
  { value: 'SUB_FEATURE', label: 'Per sub-feature' },
  { value: 'FEATURE', label: 'Per feature' },
  { value: 'TECHNOLOGY', label: 'Per technology' },
];
const QUOTA_PERIOD_OPTIONS = [
  { value: 'MONTH', label: 'Every month' },
  { value: 'NONE', label: 'One-time (never resets)' },
];
const PAID_CREDIT_SCOPE_OPTIONS = [
  { value: 'FEATURE', label: 'Pooled across the feature' },
  { value: 'TECHNOLOGY', label: 'Per technology' },
];
const PRICING_MODE_OPTIONS = [
  { value: 'FIXED', label: 'Fixed price' },
  { value: 'TIERED', label: 'Priced by tiers' },
];
const METRIC_OPTIONS = [
  { value: 'ELITE_PAID_USERS', label: 'ELITE_PAID_USERS — number of paying builders so far' },
  { value: 'ELITE_PAID_UNITS', label: 'ELITE_PAID_UNITS — number of paid units sold so far' },
];

const TABS = [
  { key: 'features', label: 'Features & free quotas', icon: Layers },
  { key: 'products', label: 'Products & pricing', icon: Package },
  { key: 'tiers', label: 'Elite pricing tiers', icon: Activity },
  { key: 'tax', label: 'Tax', icon: Percent },
  { key: 'settings', label: 'Settings', icon: Settings },
  { key: 'orders', label: 'Orders', icon: Receipt },
  { key: 'audit', label: 'Audit log', icon: History },
  { key: 'tools', label: 'Tools', icon: Wrench },
];

const ORDER_STATUS_TONES = {
  PAID: 'bg-emerald-50 text-emerald-700',
  PENDING: 'bg-amber-50 text-amber-700',
  PAYMENT_INITIATED: 'bg-blue-50 text-blue-700',
  FAILED: 'bg-red-50 text-red-600',
  CANCELLED: 'bg-gray-100 text-gray-500',
  EXPIRED: 'bg-gray-100 text-gray-500',
  REFUNDED: 'bg-orange-50 text-orange-700',
  PARTIALLY_REFUNDED: 'bg-orange-50 text-orange-700',
};

const AUDIT_ACTION_TONES = {
  INSERT: 'bg-emerald-50 text-emerald-700',
  CREATE: 'bg-emerald-50 text-emerald-700',
  UPDATE: 'bg-blue-50 text-blue-700',
  DELETE: 'bg-red-50 text-red-600',
  GRANT: 'bg-violet-50 text-violet-700',
};

/* ── small helpers ─────────────────────────────────────────────────────── */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const paiseToRupeeText = (paise) => {
  const n = num(paise);
  if (n === null) return '';
  return String(n / 100);
};

const rupeeTextToPaise = (text) => {
  const t = String(text ?? '').trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

/** ISO / postgres timestamp → the `YYYY-MM-DDTHH:mm` a datetime-local wants (local time). */
const toLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const prettyJson = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') {
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
  }
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
};

const errMessage = (e) => (e && (e.message || e.code)) || 'Request failed';

/* ── toasts ────────────────────────────────────────────────────────────── */

const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback((tone, message) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((t) => [...t, { id, tone, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 7000 : 4000);
  }, []);
  return {
    toasts,
    dismiss,
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
  };
};

const ToastStack = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-5 right-5 z-[60] space-y-2 w-[min(24rem,calc(100vw-2.5rem))]">
    {toasts.map((t) => {
      const tone = t.tone === 'error'
        ? 'bg-red-600'
        : t.tone === 'info' ? 'bg-gray-800' : 'bg-emerald-600';
      const Icon = t.tone === 'error' ? XCircle : t.tone === 'info' ? Info : CheckCircle2;
      return (
        <div key={t.id} className={`${tone} text-white rounded-xl shadow-lg px-4 py-3 flex items-start gap-2 text-sm`}>
          <Icon size={16} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1 break-words">{t.message}</span>
          <button onClick={() => onDismiss(t.id)} className="opacity-70 hover:opacity-100 flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      );
    })}
  </div>
);

/* ── field primitives ──────────────────────────────────────────────────── */

const INPUT = 'w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 focus:border-[#843FEF] disabled:bg-gray-50 disabled:text-gray-400';

const Label = ({ children, hint }) => (
  <div className="mb-1">
    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide">{children}</span>
    {hint && <span className="block text-[10px] text-gray-400 normal-case font-normal mt-0.5">{hint}</span>}
  </div>
);

const Field = ({ label, hint, children, className = '' }) => (
  <div className={className}>
    <Label hint={hint}>{label}</Label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, disabled, placeholder, type = 'text' }) => (
  <input
    type={type}
    value={value ?? ''}
    disabled={disabled}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className={INPUT}
  />
);

const NumberInput = ({ value, onChange, disabled, placeholder, nullable = false, min }) => (
  <input
    type="number"
    min={min}
    value={value === null || value === undefined ? '' : value}
    disabled={disabled}
    placeholder={placeholder}
    onChange={(e) => {
      const raw = e.target.value;
      if (raw === '') { onChange(nullable ? null : ''); return; }
      const n = Number(raw);
      onChange(Number.isFinite(n) ? n : raw);
    }}
    className={INPUT}
  />
);

const SelectInput = ({ value, onChange, options, disabled, placeholder }) => (
  <select
    value={value ?? ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    className={INPUT}
  >
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
  </select>
);

const DateTimeInput = ({ value, onChange, disabled }) => (
  <input
    type="datetime-local"
    value={toLocalInput(value)}
    disabled={disabled}
    onChange={(e) => onChange(fromLocalInput(e.target.value))}
    className={INPUT}
  />
);

const Toggle = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    title={label}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-40 ${checked ? 'bg-[#843FEF]' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[1.15rem]' : 'translate-x-0.5'}`} />
  </button>
);

/** Rupee text box that stores paise. Keeps its own text while focused so
 *  half-typed values ("12.") don't get rewritten under the cursor. */
const RupeeInput = ({ paise, onChangePaise, disabled, placeholder }) => {
  const [text, setText] = useState(paiseToRupeeText(paise));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(paiseToRupeeText(paise)); }, [paise, focused]);
  return (
    <div>
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
        <input
          type="text"
          inputMode="decimal"
          disabled={disabled}
          placeholder={placeholder}
          value={text}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); setText(paiseToRupeeText(paise)); }}
          onChange={(e) => { setText(e.target.value); onChangePaise(rupeeTextToPaise(e.target.value)); }}
          className={`${INPUT} pl-6`}
        />
      </div>
      <p className="text-[10px] text-gray-400 mt-0.5">
        {num(paise) === null ? 'not set' : `${num(paise).toLocaleString('en-IN')} paise · ${formatPaise(paise)}`}
      </p>
    </div>
  );
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}>{children}</div>
);

const SectionHeader = ({ title, subtitle, right }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
    <div>
      <h2 className="text-lg font-black text-gray-900">{title}</h2>
      {subtitle && <p className="text-xs text-gray-500 mt-1 max-w-3xl">{subtitle}</p>}
    </div>
    {right}
  </div>
);

const Pill = ({ tone, children }) => (
  <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${tone || 'bg-gray-100 text-gray-600'}`}>
    {children}
  </span>
);

const Empty = ({ children }) => (
  <div className="px-4 py-10 text-center text-sm text-gray-400">{children}</div>
);

const RowActions = ({ dirty, saving, onSave, onReset }) => (
  <div className="flex items-center gap-1 justify-end whitespace-nowrap">
    <button
      onClick={onSave}
      disabled={!dirty || saving}
      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-white text-xs font-bold shadow disabled:opacity-30"
      style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
      title={dirty ? 'Save changes' : 'No changes'}
    >
      {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
    </button>
    <button
      onClick={onReset}
      disabled={!dirty || saving}
      className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
      title="Discard changes"
    >
      <RotateCcw size={14} />
    </button>
  </div>
);

/* ── main component ────────────────────────────────────────────────────── */

const NexusBillingAdmin = () => {
  const userId = getUserId();
  const toast = useToasts();

  const [tab, setTab] = useState('features');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  /** `${table}:${key}` → partial patch of edited columns. */
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [expanded, setExpanded] = useState({}); // feature code → sub-features open

  const load = useCallback(async () => {
    if (!userId) { setError('No admin user id found in localStorage. Sign in as an admin account.'); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await billingApi.admin.getConfig(userId);
      setConfig(data);
    } catch (e) {
      setError(`${errMessage(e)}${e && e.status ? ` (HTTP ${e.status})` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const loadOrders = useCallback(async () => {
    if (!userId) return;
    setOrdersLoading(true);
    try {
      const data = await billingApi.admin.getOrders(userId, 100);
      setOrders(Array.isArray(data) ? data : (data?.orders || []));
    } catch (e) {
      toast.error(`Orders: ${errMessage(e)}`);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAudit = useCallback(async () => {
    if (!userId) return;
    setAuditLoading(true);
    try {
      const data = await billingApi.admin.getAudit(userId, 100);
      setAudit(Array.isArray(data) ? data : (data?.audit || []));
    } catch (e) {
      toast.error(`Audit log: ${errMessage(e)}`);
      setAudit([]);
    } finally {
      setAuditLoading(false);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'orders' && orders === null && !ordersLoading) loadOrders();
    if (tab === 'audit' && audit === null && !auditLoading) loadAudit();
  }, [tab, orders, audit, ordersLoading, auditLoading, loadOrders, loadAudit]);

  /* ── draft plumbing ─────────────────────────────────────────────────── */

  const draftId = (table, key) => `${table}:${key}`;

  const patchOf = useCallback((table, key) => drafts[draftId(table, key)] || null, [drafts]);

  const valueOf = useCallback((table, key, row, col) => {
    const p = drafts[draftId(table, key)];
    if (p && Object.prototype.hasOwnProperty.call(p, col)) return p[col];
    return row ? row[col] : undefined;
  }, [drafts]);

  const setValue = useCallback((table, key, col, value) => {
    setDrafts((d) => ({ ...d, [draftId(table, key)]: { ...(d[draftId(table, key)] || {}), [col]: value } }));
  }, []);

  const resetDraft = useCallback((table, key) => {
    setDrafts((d) => {
      const next = { ...d };
      delete next[draftId(table, key)];
      return next;
    });
  }, []);

  const editableCols = useCallback((table) => {
    const e = config?.editable?.[table];
    return e && Array.isArray(e.cols) ? e.cols : null;
  }, [config]);

  /** A column is locked when the server publishes a whitelist that excludes it. */
  const canEdit = useCallback((table, col) => {
    const cols = editableCols(table);
    return cols ? cols.includes(col) : true;
  }, [editableCols]);

  const saveRow = useCallback(async (table, key, { label } = {}) => {
    const patch = drafts[draftId(table, key)];
    if (!patch) return;
    const cols = editableCols(table);
    const body = {};
    Object.keys(patch).forEach((col) => { if (!cols || cols.includes(col)) body[col] = patch[col]; });
    if (Object.keys(body).length === 0) { toast.error('Nothing editable in this change.'); return; }
    setSavingKey(draftId(table, key));
    try {
      await billingApi.admin.update(userId, table, key, body);
      resetDraft(table, key);
      toast.success(`Saved ${label || key}`);
      await load();
    } catch (e) {
      toast.error(`${label || key}: ${errMessage(e)}${e && e.status === 422 ? ' (rejected by server validation)' : ''}`);
    } finally {
      setSavingKey(null);
    }
  }, [drafts, editableCols, userId, resetDraft, load]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── derived slices of the config ───────────────────────────────────── */

  const features = useMemo(
    () => [...(config?.features || [])].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [config],
  );
  const subFeatures = config?.subFeatures || [];
  const products = useMemo(
    () => [...(config?.products || [])].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [config],
  );
  const tiers = config?.tiers || [];
  const taxRates = config?.taxRates || [];
  const technologies = useMemo(() => config?.technologies || [], [config]);
  const counters = config?.counters || [];
  const settings = config?.settings || {};

  const technologyOptions = useMemo(
    () => technologies.map((t) => ({ value: t.slug, label: t.title || t.name || t.slug })),
    [technologies],
  );

  const featureName = useCallback(
    (code) => features.find((f) => f.code === code)?.name || code,
    [features],
  );

  /** "Free prompts every month, per sub-feature" — built from the row, never hardcoded. */
  const quotaSentence = (f) => {
    const unit = f.free_quota === 1 ? (f.unit_label || 'unit') : (f.unit_label_plural || f.unit_label || 'units');
    const period = f.quota_period === 'MONTH' ? 'every month' : 'once (never resets)';
    const scope = f.quota_scope === 'SUB_FEATURE'
      ? `per ${f.name || f.code} sub-feature`
      : f.quota_scope === 'TECHNOLOGY' ? 'per technology' : `across all of ${f.name || f.code}`;
    return `${f.free_quota ?? 0} free ${unit} ${period}, ${scope}.`;
  };

  /* ── tab bodies ─────────────────────────────────────────────────────── */

  const renderFeatures = () => (
    <>
      <SectionHeader
        title="Features & free quotas"
        subtitle="Every billable capability in TSG Nexus and the free allowance a builder gets before they have to buy credits. The quota scope decides what the allowance is counted against, and the quota period decides whether it refills."
      />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1050px]">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[11px] tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 w-8"></th>
                <th className="text-left px-4 py-3">Feature</th>
                <th className="text-left px-4 py-3 w-52">Display name</th>
                <th className="text-left px-4 py-3 w-28">Free quota</th>
                <th className="text-left px-4 py-3 w-44">Quota scope</th>
                <th className="text-left px-4 py-3 w-44">Quota period</th>
                <th className="text-left px-4 py-3 w-44">Paid credit scope</th>
                <th className="text-left px-4 py-3 w-20">Order</th>
                <th className="text-left px-4 py-3 w-20">Active</th>
                <th className="text-right px-4 py-3 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {features.length === 0 && (
                <tr><td colSpan={10}><Empty>No billing features configured.</Empty></td></tr>
              )}
              {features.map((f) => {
                const key = f.code;
                const id = draftId(T.FEATURES, key);
                const dirty = !!patchOf(T.FEATURES, key);
                const v = (col) => valueOf(T.FEATURES, key, f, col);
                const set = (col, value) => setValue(T.FEATURES, key, col, value);
                const kids = subFeatures
                  .filter((s) => s.feature_code === f.code)
                  .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
                const open = !!expanded[f.code];
                const preview = { ...f, ...(patchOf(T.FEATURES, key) || {}) };
                return (
                  <React.Fragment key={key}>
                    <tr className="border-t border-gray-100 align-top">
                      <td className="px-4 py-3">
                        {kids.length > 0 && (
                          <button
                            onClick={() => setExpanded((e) => ({ ...e, [f.code]: !e[f.code] }))}
                            className="p-1 rounded hover:bg-gray-100 text-gray-400"
                            title={open ? 'Hide sub-features' : `Show ${kids.length} sub-features`}
                          >
                            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-bold text-gray-900">{f.code}</div>
                        <div className="text-[11px] text-gray-400 max-w-xs">{f.description}</div>
                        <div className="text-[11px] text-[#5B21B6] mt-1">{quotaSentence(preview)}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          Unit: {f.unit_label} / {f.unit_label_plural}
                          {kids.length > 0 && ` · ${kids.length} sub-feature${kids.length === 1 ? '' : 's'}`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <TextInput value={v('name')} onChange={(x) => set('name', x)} disabled={!canEdit(T.FEATURES, 'name')} />
                      </td>
                      <td className="px-4 py-3">
                        <NumberInput min={0} value={v('free_quota')} onChange={(x) => set('free_quota', x)} disabled={!canEdit(T.FEATURES, 'free_quota')} />
                        <p className="text-[10px] text-gray-400 mt-0.5">Free {f.unit_label_plural || 'units'} before a purchase is required.</p>
                      </td>
                      <td className="px-4 py-3">
                        <SelectInput value={v('quota_scope')} onChange={(x) => set('quota_scope', x)} options={QUOTA_SCOPE_OPTIONS} disabled={!canEdit(T.FEATURES, 'quota_scope')} />
                        <p className="text-[10px] text-gray-400 mt-0.5">What the free quota is counted per: each sub-feature gets its own bucket, the whole feature shares one, or each technology gets one.</p>
                      </td>
                      <td className="px-4 py-3">
                        <SelectInput value={v('quota_period')} onChange={(x) => set('quota_period', x)} options={QUOTA_PERIOD_OPTIONS} disabled={!canEdit(T.FEATURES, 'quota_period')} />
                        <p className="text-[10px] text-gray-400 mt-0.5">Monthly quotas reset on the 1st in the billing timezone; one-time quotas never come back.</p>
                      </td>
                      <td className="px-4 py-3">
                        <SelectInput value={v('paid_credit_scope')} onChange={(x) => set('paid_credit_scope', x)} options={PAID_CREDIT_SCOPE_OPTIONS} disabled={!canEdit(T.FEATURES, 'paid_credit_scope')} />
                        <p className="text-[10px] text-gray-400 mt-0.5">Whether purchased credits are usable anywhere in the feature or only on the technology they were bought for.</p>
                      </td>
                      <td className="px-4 py-3">
                        <NumberInput value={v('display_order')} onChange={(x) => set('display_order', x)} disabled={!canEdit(T.FEATURES, 'display_order')} />
                      </td>
                      <td className="px-4 py-3">
                        <Toggle checked={!!v('is_active')} onChange={(x) => set('is_active', x)} disabled={!canEdit(T.FEATURES, 'is_active')} label="Active" />
                        <p className="text-[10px] text-gray-400 mt-1">{v('is_active') ? 'Billable & visible' : 'Hidden from builders'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <RowActions
                          dirty={dirty}
                          saving={savingKey === id}
                          onSave={() => saveRow(T.FEATURES, key, { label: f.code })}
                          onReset={() => resetDraft(T.FEATURES, key)}
                        />
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-gray-50/60 border-t border-gray-100">
                        <td></td>
                        <td colSpan={9} className="px-4 py-4">
                          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                            {f.name} sub-features
                          </p>
                          <p className="text-[11px] text-gray-400 mb-3">
                            {preview.quota_scope === 'SUB_FEATURE'
                              ? `Each of these gets its own free allowance of ${preview.free_quota ?? 0} ${preview.unit_label_plural || 'units'}.`
                              : 'These share the feature-level free allowance.'}{' '}
                            The route key is what the backend matches an incoming request against — change it only alongside a backend change.
                          </p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[720px] bg-white rounded-xl border border-gray-100">
                              <thead className="text-gray-500 uppercase text-[10px] tracking-wide">
                                <tr>
                                  <th className="text-left px-3 py-2">Code</th>
                                  <th className="text-left px-3 py-2 w-56">Name</th>
                                  <th className="text-left px-3 py-2 w-48">Route key</th>
                                  <th className="text-left px-3 py-2 w-20">Order</th>
                                  <th className="text-left px-3 py-2 w-20">Active</th>
                                  <th className="text-right px-3 py-2 w-32">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {kids.map((s) => {
                                  const sk = s.code;
                                  const sid = draftId(T.SUB_FEATURES, sk);
                                  const sv = (col) => valueOf(T.SUB_FEATURES, sk, s, col);
                                  const sset = (col, value) => setValue(T.SUB_FEATURES, sk, col, value);
                                  return (
                                    <tr key={sk} className="border-t border-gray-100">
                                      <td className="px-3 py-2 font-mono text-xs text-gray-600">{s.code}</td>
                                      <td className="px-3 py-2">
                                        <TextInput value={sv('name')} onChange={(x) => sset('name', x)} disabled={!canEdit(T.SUB_FEATURES, 'name')} />
                                      </td>
                                      <td className="px-3 py-2">
                                        <TextInput value={sv('route_key')} onChange={(x) => sset('route_key', x)} disabled={!canEdit(T.SUB_FEATURES, 'route_key')} />
                                      </td>
                                      <td className="px-3 py-2">
                                        <NumberInput value={sv('display_order')} onChange={(x) => sset('display_order', x)} disabled={!canEdit(T.SUB_FEATURES, 'display_order')} />
                                      </td>
                                      <td className="px-3 py-2">
                                        <Toggle checked={!!sv('is_active')} onChange={(x) => sset('is_active', x)} disabled={!canEdit(T.SUB_FEATURES, 'is_active')} label="Active" />
                                      </td>
                                      <td className="px-3 py-2">
                                        <RowActions
                                          dirty={!!patchOf(T.SUB_FEATURES, sk)}
                                          saving={savingKey === sid}
                                          onSave={() => saveRow(T.SUB_FEATURES, sk, { label: s.code })}
                                          onReset={() => resetDraft(T.SUB_FEATURES, sk)}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );

  const renderProducts = () => (
    <>
      <SectionHeader
        title="Products & pricing"
        subtitle="What a builder can actually buy. Prices are stored in paise — type rupees below and the paise value stored is shown under each box. Tiered products take their unit price from the Elite pricing tiers tab instead of a base price."
      />
      <div className="space-y-4">
        {products.length === 0 && <Card><Empty>No billing products configured.</Empty></Card>}
        {products.map((p) => {
          const key = p.code;
          const id = draftId(T.PRODUCTS, key);
          const v = (col) => valueOf(T.PRODUCTS, key, p, col);
          const set = (col, value) => setValue(T.PRODUCTS, key, col, value);
          const mode = v('pricing_mode');
          const unitsPer = num(v('units_per_purchase'));
          const basePaise = num(v('base_price_paise'));
          return (
            <Card key={key} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold text-gray-900">{p.code}</span>
                    <Pill tone="bg-violet-50 text-violet-700">{featureName(p.feature_code)}</Pill>
                    {p.requires_technology && <Pill tone="bg-amber-50 text-amber-700">needs a technology</Pill>}
                    <Pill tone={v('is_active') ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                      {v('is_active') ? 'active' : 'inactive'}
                    </Pill>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Currency {p.currency}
                    {mode === 'FIXED' && basePaise !== null && unitsPer
                      ? ` · one purchase = ${unitsPer.toLocaleString('en-IN')} ${featureUnit(p)} for ${formatPaise(basePaise)} (${formatPaise(basePaise / unitsPer)} each, before tax)`
                      : ''}
                  </p>
                </div>
                <RowActions
                  dirty={!!patchOf(T.PRODUCTS, key)}
                  saving={savingKey === id}
                  onSave={() => saveRow(T.PRODUCTS, key, { label: p.code })}
                  onReset={() => resetDraft(T.PRODUCTS, key)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Name" className="lg:col-span-2">
                  <TextInput value={v('name')} onChange={(x) => set('name', x)} disabled={!canEdit(T.PRODUCTS, 'name')} />
                </Field>
                <Field label="Units per purchase" hint={`How many ${featureUnit(p)} one unit of this product grants.`}>
                  <NumberInput min={1} value={v('units_per_purchase')} onChange={(x) => set('units_per_purchase', x)} disabled={!canEdit(T.PRODUCTS, 'units_per_purchase')} />
                </Field>
                <Field label="Pricing mode" hint="Fixed uses the base price below; tiered reads the tier table.">
                  <SelectInput value={mode} onChange={(x) => set('pricing_mode', x)} options={PRICING_MODE_OPTIONS} disabled={!canEdit(T.PRODUCTS, 'pricing_mode')} />
                </Field>

                <Field label="Description" className="md:col-span-2 lg:col-span-4">
                  <textarea
                    rows={2}
                    value={v('description') ?? ''}
                    disabled={!canEdit(T.PRODUCTS, 'description')}
                    onChange={(e) => set('description', e.target.value)}
                    className={INPUT}
                  />
                </Field>

                <Field label="Base price" hint="Price per single purchase, excluding tax.">
                  {mode === 'TIERED' ? (
                    <div className="px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-800 text-xs font-semibold border border-amber-100">
                      Priced by tiers — see the Elite pricing tiers tab
                    </div>
                  ) : (
                    <RupeeInput
                      paise={v('base_price_paise')}
                      onChangePaise={(x) => set('base_price_paise', x)}
                      disabled={!canEdit(T.PRODUCTS, 'base_price_paise')}
                      placeholder="0.00"
                    />
                  )}
                </Field>
                <Field label="Max quantity per order" hint="Cap on how many of this product one order may contain.">
                  <NumberInput min={1} value={v('max_quantity_per_order')} onChange={(x) => set('max_quantity_per_order', x)} disabled={!canEdit(T.PRODUCTS, 'max_quantity_per_order')} />
                </Field>
                <Field label="Entitlement validity (days)" hint="Blank = credits never expire.">
                  <NumberInput nullable min={1} value={v('entitlement_validity_days')} onChange={(x) => set('entitlement_validity_days', x)} disabled={!canEdit(T.PRODUCTS, 'entitlement_validity_days')} placeholder="never expires" />
                </Field>
                <Field label="Active" hint="Inactive products disappear from the builder catalog immediately.">
                  <div className="pt-1"><Toggle checked={!!v('is_active')} onChange={(x) => set('is_active', x)} disabled={!canEdit(T.PRODUCTS, 'is_active')} label="Active" /></div>
                </Field>

                <Field label="Effective from" hint="When this product becomes purchasable.">
                  <DateTimeInput value={v('effective_from')} onChange={(x) => set('effective_from', x)} disabled={!canEdit(T.PRODUCTS, 'effective_from')} />
                </Field>
                <Field label="Effective to" hint="Blank = open-ended.">
                  <DateTimeInput value={v('effective_to')} onChange={(x) => set('effective_to', x)} disabled={!canEdit(T.PRODUCTS, 'effective_to')} />
                </Field>
                <Field label="Display order">
                  <NumberInput value={v('display_order')} onChange={(x) => set('display_order', x)} disabled={!canEdit(T.PRODUCTS, 'display_order')} />
                </Field>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );

  /** unit label of the feature a product belongs to (for helper copy). */
  function featureUnit(p) {
    const f = features.find((x) => x.code === p.feature_code);
    return f?.unit_label_plural || f?.unit_label || 'units';
  }

  const renderTiers = () => {
    const sorted = [...tiers].sort((a, b) => {
      if ((a.product_code || '') !== (b.product_code || '')) return String(a.product_code).localeCompare(String(b.product_code));
      const at = a.technology_slug || '';
      const bt = b.technology_slug || '';
      if (at !== bt) return at.localeCompare(bt);
      return (num(a.min_threshold) ?? 0) - (num(b.min_threshold) ?? 0);
    });

    /* Client-side sanity check per (product, technology, metric) group. The
       server is still the authority — this only warns early. */
    const warnings = [];
    const groups = {};
    sorted.forEach((t) => {
      const g = `${t.product_code}|${t.technology_slug || '*'}|${t.metric_key}`;
      (groups[g] = groups[g] || []).push(t);
    });
    Object.entries(groups).forEach(([g, rows]) => {
      const [prod, tech, metric] = g.split('|');
      const label = `${prod} · ${tech === '*' ? 'all technologies' : tech} · ${metric}`;
      const live = rows.filter((r) => r.is_active).sort((a, b) => (num(a.min_threshold) ?? 0) - (num(b.min_threshold) ?? 0));
      if (live.length === 0) { warnings.push(`${label}: no active tier — this product cannot be priced.`); return; }
      if ((num(live[0].min_threshold) ?? 0) > 1) {
        warnings.push(`${label}: the lowest tier starts at ${live[0].min_threshold}, so values below that have no price.`);
      }
      for (let i = 0; i < live.length - 1; i += 1) {
        const cur = live[i];
        const nxt = live[i + 1];
        const curMax = num(cur.max_threshold);
        const nxtMin = num(nxt.min_threshold) ?? 0;
        if (curMax === null) {
          warnings.push(`${label}: "${cur.tier_name}" is open-ended, so "${nxt.tier_name}" can never be reached.`);
          break;
        }
        if (nxtMin <= curMax) warnings.push(`${label}: "${cur.tier_name}" (…${curMax}) overlaps "${nxt.tier_name}" (${nxtMin}…).`);
        else if (nxtMin > curMax + 1) warnings.push(`${label}: gap between ${curMax} and ${nxtMin} — "${cur.tier_name}" and "${nxt.tier_name}" leave ${nxtMin - curMax - 1} value(s) unpriced.`);
      }
      const last = live[live.length - 1];
      if (num(last.max_threshold) !== null) warnings.push(`${label}: the highest tier ends at ${last.max_threshold} — anything above it has no price. Leave "To" blank to make it open-ended.`);
    });

    const counterValue = (k) => {
      const c = counters.find((x) => x.counter_key === k);
      return c ? num(c.current_value) : null;
    };

    /* "Next new buyer pays …" preview, per (product, technology) group that
       prices off a counter metric. */
    const previews = Object.entries(groups).map(([g, rows]) => {
      const [prod, tech, metric] = g.split('|');
      const cur = counterValue(metric);
      if (cur === null) return null;
      const next = cur + 1;
      const match = rows
        .filter((r) => r.is_active)
        .sort((a, b) => (num(a.min_threshold) ?? 0) - (num(b.min_threshold) ?? 0))
        .find((r) => next >= (num(r.min_threshold) ?? 0) && (num(r.max_threshold) === null || next <= num(r.max_threshold)));
      return { group: g, product: prod, technology: tech, metric, current: cur, next, tier: match };
    }).filter(Boolean);

    return (
      <>
        <SectionHeader
          title="Elite pricing tiers"
          subtitle="Tiered products get their unit price from whichever tier the live metric currently falls into — early buyers pay the founding price, later ones move up. A tier with no upper bound catches everything above it."
          right={
            <button
              onClick={() => setTierForm((f) => ({ ...f, open: !f.open }))}
              className="px-4 py-2 rounded-lg text-white flex items-center gap-2 text-sm font-bold shadow"
              style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
            >
              <Plus size={16} /> Add tier
            </button>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Card className="p-5 lg:col-span-1">
            <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-wide mb-3">Live counters</h3>
            {counters.length === 0 ? (
              <p className="text-sm text-gray-400">No counters yet.</p>
            ) : (
              <div className="space-y-2">
                {counters.map((c) => (
                  <div key={c.counter_key} className="flex items-baseline justify-between gap-2">
                    <div>
                      <div className="font-mono text-xs text-gray-600">{c.counter_key}</div>
                      <div className="text-[10px] text-gray-400">updated {formatDateTime(c.updated_at)}</div>
                    </div>
                    <div className="text-xl font-black text-[#5B21B6]">{num(c.current_value)?.toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5 lg:col-span-2">
            <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-wide mb-3">Next purchase preview</h3>
            {previews.length === 0 ? (
              <p className="text-sm text-gray-400">No tier group has a live counter to price against.</p>
            ) : (
              <div className="space-y-2">
                {previews.map((p) => (
                  <div key={p.group} className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                    <div className="text-xs text-gray-500">
                      <span className="font-mono text-gray-700">{p.product}</span>
                      {' · '}
                      {p.technology === '*' ? 'all technologies' : (technologyOptions.find((t) => t.value === p.technology)?.label || p.technology)}
                      <div className="text-[10px] text-gray-400">
                        {p.metric} is {p.current.toLocaleString('en-IN')} → next is {p.next.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="text-sm font-bold text-gray-900">
                      {p.tier
                        ? <>Next new buyer pays {formatPaise(p.tier.unit_price_paise)} <span className="text-[#5B21B6]">(tier “{p.tier.tier_name}”)</span></>
                        : <span className="text-red-600">No active tier covers {p.next} — a purchase would be refused.</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {warnings.length > 0 && (
          <div className="mb-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded text-amber-800 text-xs">
            <div className="flex items-center gap-2 font-bold mb-1"><AlertTriangle size={14} /> Tier coverage warnings (checked in the browser — the server is still the authority)</div>
            <ul className="list-disc pl-5 space-y-0.5">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {tierForm.open && renderTierForm()}

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1150px]">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[11px] tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Product</th>
                  <th className="text-left px-4 py-3 w-44">Tier name</th>
                  <th className="text-left px-4 py-3 w-44">Technology</th>
                  <th className="text-left px-4 py-3 w-40">Metric</th>
                  <th className="text-left px-4 py-3 w-24">From</th>
                  <th className="text-left px-4 py-3 w-24">To</th>
                  <th className="text-left px-4 py-3 w-40">Unit price</th>
                  <th className="text-left px-4 py-3 w-32">Units override</th>
                  <th className="text-left px-4 py-3 w-20">Active</th>
                  <th className="text-right px-4 py-3 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && <tr><td colSpan={10}><Empty>No pricing tiers yet.</Empty></td></tr>}
                {sorted.map((t) => {
                  const key = String(t.id);
                  const id = draftId(T.TIERS, key);
                  const v = (col) => valueOf(T.TIERS, key, t, col);
                  const set = (col, value) => setValue(T.TIERS, key, col, value);
                  return (
                    <tr key={key} className="border-t border-gray-100 align-top">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-gray-700">{t.product_code}</div>
                        <div className="text-[10px] text-gray-400">#{t.id} · {t.currency}</div>
                      </td>
                      <td className="px-4 py-3">
                        <TextInput value={v('tier_name')} onChange={(x) => set('tier_name', x)} disabled={!canEdit(T.TIERS, 'tier_name')} />
                      </td>
                      <td className="px-4 py-3">
                        <SelectInput
                          value={v('technology_slug')}
                          onChange={(x) => set('technology_slug', x)}
                          options={technologyOptions}
                          placeholder="All technologies"
                          disabled={!canEdit(T.TIERS, 'technology_slug')}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <SelectInput value={v('metric_key')} onChange={(x) => set('metric_key', x)} options={METRIC_OPTIONS.map((m) => ({ value: m.value, label: m.value }))} disabled={!canEdit(T.TIERS, 'metric_key')} />
                      </td>
                      <td className="px-4 py-3">
                        <NumberInput min={0} value={v('min_threshold')} onChange={(x) => set('min_threshold', x)} disabled={!canEdit(T.TIERS, 'min_threshold')} />
                      </td>
                      <td className="px-4 py-3">
                        <NumberInput nullable min={0} value={v('max_threshold')} onChange={(x) => set('max_threshold', x)} disabled={!canEdit(T.TIERS, 'max_threshold')} placeholder="∞" />
                        <p className="text-[10px] text-gray-400 mt-0.5">blank = ∞</p>
                      </td>
                      <td className="px-4 py-3">
                        <RupeeInput paise={v('unit_price_paise')} onChangePaise={(x) => set('unit_price_paise', x)} disabled={!canEdit(T.TIERS, 'unit_price_paise')} />
                      </td>
                      <td className="px-4 py-3">
                        <NumberInput nullable min={1} value={v('units_per_purchase')} onChange={(x) => set('units_per_purchase', x)} disabled={!canEdit(T.TIERS, 'units_per_purchase')} placeholder="product default" />
                      </td>
                      <td className="px-4 py-3">
                        <Toggle checked={!!v('is_active')} onChange={(x) => set('is_active', x)} disabled={!canEdit(T.TIERS, 'is_active')} label="Active" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <RowActions
                            dirty={!!patchOf(T.TIERS, key)}
                            saving={savingKey === id}
                            onSave={() => saveRow(T.TIERS, key, { label: `tier ${t.tier_name}` })}
                            onReset={() => resetDraft(T.TIERS, key)}
                          />
                          <button
                            onClick={() => handleDeleteTier(t)}
                            disabled={deletingTier === t.id}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-40"
                            title="Delete tier"
                          >
                            {deletingTier === t.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                        <div className="text-[10px] text-gray-400 text-right mt-1">
                          {t.effective_from ? `from ${formatDateTime(t.effective_from)}` : ''}
                          {t.effective_to ? ` · to ${formatDateTime(t.effective_to)}` : ''}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </>
    );
  };

  /* ── add-tier form ──────────────────────────────────────────────────── */

  const emptyTierForm = {
    open: false,
    product_code: '',
    technology_slug: '',
    metric_key: METRIC_OPTIONS[0].value,
    tier_name: '',
    min_threshold: '',
    max_threshold: '',
    unit_price_paise: null,
    units_per_purchase: '',
  };
  const [tierForm, setTierForm] = useState(emptyTierForm);
  const [creatingTier, setCreatingTier] = useState(false);
  const [deletingTier, setDeletingTier] = useState(null);

  const handleCreateTier = async (e) => {
    e.preventDefault();
    if (!tierForm.product_code || !tierForm.tier_name.trim() || tierForm.min_threshold === '' || tierForm.unit_price_paise === null) {
      toast.error('Product, tier name, "from" threshold and unit price are all required.');
      return;
    }
    setCreatingTier(true);
    try {
      await billingApi.admin.createTier(userId, {
        product_code: tierForm.product_code,
        technology_slug: tierForm.technology_slug || null,
        metric_key: tierForm.metric_key,
        tier_name: tierForm.tier_name.trim(),
        min_threshold: Number(tierForm.min_threshold),
        max_threshold: tierForm.max_threshold === '' ? null : Number(tierForm.max_threshold),
        unit_price_paise: tierForm.unit_price_paise,
        units_per_purchase: tierForm.units_per_purchase === '' ? null : Number(tierForm.units_per_purchase),
      });
      setTierForm(emptyTierForm);
      toast.success('Tier created');
      await load();
    } catch (err) {
      toast.error(`Create tier: ${errMessage(err)}`);
    } finally {
      setCreatingTier(false);
    }
  };

  const handleDeleteTier = async (t) => {
    if (!window.confirm(`Delete tier "${t.tier_name}" (${t.product_code}${t.technology_slug ? ` · ${t.technology_slug}` : ''})? Pricing falls back to whatever other tier covers that range.`)) return;
    setDeletingTier(t.id);
    try {
      await billingApi.admin.deleteTier(userId, t.id);
      toast.success(`Deleted tier "${t.tier_name}"`);
      await load();
    } catch (err) {
      toast.error(`Delete tier: ${errMessage(err)}`);
    } finally {
      setDeletingTier(null);
    }
  };

  const renderTierForm = () => (
    <Card className="p-5 mb-4 border-[#843FEF]/30">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-black text-gray-900">Add pricing tier</h3>
        <button onClick={() => setTierForm(emptyTierForm)} className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
      </div>
      <form onSubmit={handleCreateTier} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Product" hint="Only tiered products read this table.">
          <SelectInput
            value={tierForm.product_code}
            onChange={(x) => setTierForm((f) => ({ ...f, product_code: x || '' }))}
            options={(products.some((p) => p.pricing_mode === 'TIERED')
              ? products.filter((p) => p.pricing_mode === 'TIERED')
              : products
            ).map((p) => ({ value: p.code, label: `${p.name} (${p.code})` }))}
            placeholder="Select a product"
          />
        </Field>
        <Field label="Technology" hint="Leave as All to price every technology the same.">
          <SelectInput
            value={tierForm.technology_slug}
            onChange={(x) => setTierForm((f) => ({ ...f, technology_slug: x || '' }))}
            options={technologyOptions}
            placeholder="All technologies"
          />
        </Field>
        <Field label="Metric" hint="The live counter this tier is matched against.">
          <SelectInput
            value={tierForm.metric_key}
            onChange={(x) => setTierForm((f) => ({ ...f, metric_key: x || METRIC_OPTIONS[0].value }))}
            options={METRIC_OPTIONS}
          />
        </Field>
        <Field label="Tier name" hint="Shown to builders on the price.">
          <TextInput value={tierForm.tier_name} onChange={(x) => setTierForm((f) => ({ ...f, tier_name: x }))} />
        </Field>
        <Field label="From (min threshold)" hint="Inclusive lower bound of the metric.">
          <NumberInput min={0} value={tierForm.min_threshold} onChange={(x) => setTierForm((f) => ({ ...f, min_threshold: x }))} />
        </Field>
        <Field label="To (max threshold)" hint="Inclusive upper bound. Blank = open-ended.">
          <NumberInput nullable min={0} value={tierForm.max_threshold === '' ? null : tierForm.max_threshold} onChange={(x) => setTierForm((f) => ({ ...f, max_threshold: x === null ? '' : x }))} placeholder="∞" />
        </Field>
        <Field label="Unit price" hint="Price of one unit at this tier, excluding tax.">
          <RupeeInput paise={tierForm.unit_price_paise} onChangePaise={(x) => setTierForm((f) => ({ ...f, unit_price_paise: x }))} placeholder="0.00" />
        </Field>
        <Field label="Units per purchase override" hint="Blank = use the product's value.">
          <NumberInput nullable min={1} value={tierForm.units_per_purchase === '' ? null : tierForm.units_per_purchase} onChange={(x) => setTierForm((f) => ({ ...f, units_per_purchase: x === null ? '' : x }))} placeholder="product default" />
        </Field>
        <div className="md:col-span-2 lg:col-span-4 flex gap-3">
          <button
            type="submit"
            disabled={creatingTier}
            className="px-5 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
          >
            {creatingTier ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create tier
          </button>
          <button type="button" onClick={() => setTierForm(emptyTierForm)} className="px-5 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 text-sm">
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );

  /* ── tax ────────────────────────────────────────────────────────────── */

  const renderTax = () => (
    <>
      <SectionHeader
        title="Tax"
        subtitle="Tax is stored in basis points (1800 bps = 18%). Type a percent below and the stored bps value is shown underneath. The default rate is the one applied when a product does not name its own."
      />
      <div className="space-y-4">
        {taxRates.length === 0 && <Card><Empty>No tax rates configured.</Empty></Card>}
        {taxRates.map((r) => {
          const key = r.code;
          const id = draftId(T.TAX, key);
          const v = (col) => valueOf(T.TAX, key, r, col);
          const set = (col, value) => setValue(T.TAX, key, col, value);
          const bps = num(v('rate_bps'));
          return (
            <Card key={key} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-gray-900">{r.code}</span>
                  {v('is_default') && <Pill tone="bg-violet-50 text-violet-700">default</Pill>}
                  <Pill tone={v('is_active') ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                    {v('is_active') ? 'active' : 'inactive'}
                  </Pill>
                  <span className="text-xs text-gray-400">{bps === null ? '' : `${bps} bps = ${bps / 100}%`}</span>
                </div>
                <RowActions
                  dirty={!!patchOf(T.TAX, key)}
                  saving={savingKey === id}
                  onSave={() => saveRow(T.TAX, key, { label: r.code })}
                  onReset={() => resetDraft(T.TAX, key)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Name" className="lg:col-span-2">
                  <TextInput value={v('name')} onChange={(x) => set('name', x)} disabled={!canEdit(T.TAX, 'name')} />
                </Field>
                <Field label="Rate (%)" hint="Stored as basis points.">
                  <PercentInput bps={v('rate_bps')} onChangeBps={(x) => set('rate_bps', x)} disabled={!canEdit(T.TAX, 'rate_bps')} />
                </Field>
                <div className="flex flex-col gap-3">
                  <Field label="Default" hint="Applied when nothing else is specified.">
                    <div className="pt-1"><Toggle checked={!!v('is_default')} onChange={(x) => set('is_default', x)} disabled={!canEdit(T.TAX, 'is_default')} label="Default" /></div>
                  </Field>
                  <Field label="Active">
                    <div className="pt-1"><Toggle checked={!!v('is_active')} onChange={(x) => set('is_active', x)} disabled={!canEdit(T.TAX, 'is_active')} label="Active" /></div>
                  </Field>
                </div>
                <Field label="Effective from">
                  <DateTimeInput value={v('effective_from')} onChange={(x) => set('effective_from', x)} disabled={!canEdit(T.TAX, 'effective_from')} />
                </Field>
                <Field label="Effective to" hint="Blank = open-ended.">
                  <DateTimeInput value={v('effective_to')} onChange={(x) => set('effective_to', x)} disabled={!canEdit(T.TAX, 'effective_to')} />
                </Field>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );

  /* ── settings ───────────────────────────────────────────────────────── */

  const renderSettings = () => {
    const keys = Object.keys(settings).sort();
    return (
      <>
        <SectionHeader
          title="Settings"
          subtitle={'Raw billing settings, stored as JSON values — strings must be quoted ("Asia/Kolkata"), numbers are written bare (900), booleans are true / false. Invalid JSON is refused before it is sent.'}
        />
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[11px] tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3 w-80">Setting</th>
                  <th className="text-left px-4 py-3">Value (JSON)</th>
                  <th className="text-right px-4 py-3 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 && <tr><td colSpan={3}><Empty>No settings returned.</Empty></td></tr>}
                {keys.map((k) => {
                  const id = draftId(T.SETTINGS, k);
                  const readOnly = k === 'billing.config_version' || !canEdit(T.SETTINGS, 'setting_value');
                  const stored = (() => { try { return JSON.stringify(settings[k]); } catch { return String(settings[k]); } })();
                  const draft = drafts[id];
                  const text = draft && Object.prototype.hasOwnProperty.call(draft, '__text') ? draft.__text : stored;
                  let parseError = null;
                  if (draft) { try { JSON.parse(text); } catch (e) { parseError = 'Not valid JSON'; } }
                  return (
                    <tr key={k} className="border-t border-gray-100 align-top">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-bold text-gray-800 break-all">{k}</div>
                        {readOnly && <div className="text-[10px] text-gray-400 mt-0.5">read-only — maintained by the server</div>}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={text}
                          disabled={readOnly}
                          onChange={(e) => setDrafts((d) => ({ ...d, [id]: { __text: e.target.value } }))}
                          className={`${INPUT} font-mono`}
                        />
                        {parseError && <p className="text-[10px] text-red-600 mt-0.5">{parseError}</p>}
                        {!draft && <p className="text-[10px] text-gray-400 mt-0.5">stored type: {settings[k] === null ? 'null' : typeof settings[k]}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <RowActions
                          dirty={!!draft && !parseError}
                          saving={savingKey === id}
                          onSave={() => handleSaveSetting(k, text)}
                          onReset={() => resetDraft(T.SETTINGS, k)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </>
    );
  };

  const handleSaveSetting = async (key, text) => {
    let parsed;
    try { parsed = JSON.parse(text); } catch { toast.error(`${key}: value must be valid JSON.`); return; }
    setSavingKey(draftId(T.SETTINGS, key));
    try {
      await billingApi.admin.update(userId, T.SETTINGS, key, { setting_value: parsed });
      resetDraft(T.SETTINGS, key);
      toast.success(`Saved ${key}`);
      await load();
    } catch (e) {
      toast.error(`${key}: ${errMessage(e)}`);
    } finally {
      setSavingKey(null);
    }
  };

  /* ── orders ─────────────────────────────────────────────────────────── */

  const renderOrders = () => (
    <>
      <SectionHeader
        title="Recent orders"
        subtitle="Every TSG Nexus order, newest first, exactly as the billing service recorded it."
        right={
          <button onClick={loadOrders} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-2 text-sm font-semibold">
            <RefreshCw size={16} className={ordersLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[11px] tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Paid at</th>
              </tr>
            </thead>
            <tbody>
              {ordersLoading && <tr><td colSpan={7}><Empty><Loader2 size={18} className="animate-spin inline" /> Loading orders…</Empty></td></tr>}
              {!ordersLoading && orders && orders.length === 0 && <tr><td colSpan={7}><Empty>No orders yet.</Empty></td></tr>}
              {!ordersLoading && (orders || []).map((o) => (
                <tr key={o.id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs font-bold text-gray-900">{o.order_number}</div>
                    <div className="text-[10px] text-gray-400">#{o.id} · {formatDateTime(o.created_at)}</div>
                    {o.provider_payment_id && <div className="text-[10px] text-gray-400 font-mono">{o.provider_payment_id}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-800">{o.full_name || '—'}</div>
                    <div className="text-[11px] text-gray-400 break-all">{o.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {(o.items || []).length === 0 ? <span className="text-gray-400">—</span> : (
                      <ul className="space-y-0.5">
                        {(o.items || []).map((it, i) => (
                          <li key={i} className="text-[11px] text-gray-600">
                            <span className="font-semibold text-gray-800">{it.product}</span>
                            {it.technology ? ` · ${it.technology}` : ''}
                            {` — ${it.quantity} × ${formatPaise(it.unitPricePaise)}`}
                            {it.unitsTotal ? ` (${Number(it.unitsTotal).toLocaleString('en-IN')} units)` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-bold text-gray-900">{formatPaise(o.grand_total_paise)}</div>
                    <div className="text-[10px] text-gray-400">
                      {formatPaise(o.subtotal_paise)} + {formatPaise(o.tax_amount_paise)} tax
                    </div>
                  </td>
                  <td className="px-4 py-3"><Pill tone={ORDER_STATUS_TONES[o.status]}>{o.status}</Pill></td>
                  <td className="px-4 py-3 font-mono text-[11px] text-gray-600">{o.invoice_number || '—'}</td>
                  <td className="px-4 py-3 text-[11px] text-gray-600">{formatDateTime(o.paid_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );

  /* ── audit ──────────────────────────────────────────────────────────── */

  const renderAudit = () => (
    <>
      <SectionHeader
        title="Configuration audit log"
        subtitle="Every change made to billing configuration, with the exact before and after values."
        right={
          <button onClick={loadAudit} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-2 text-sm font-semibold">
            <RefreshCw size={16} className={auditLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[11px] tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 w-56">When / who</th>
                <th className="text-left px-4 py-3 w-56">Record</th>
                <th className="text-left px-4 py-3">Before</th>
                <th className="text-left px-4 py-3">After</th>
              </tr>
            </thead>
            <tbody>
              {auditLoading && <tr><td colSpan={4}><Empty><Loader2 size={18} className="animate-spin inline" /> Loading audit log…</Empty></td></tr>}
              {!auditLoading && audit && audit.length === 0 && <tr><td colSpan={4}><Empty>Nothing has been changed yet.</Empty></td></tr>}
              {!auditLoading && (audit || []).map((a) => (
                <tr key={a.id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-800">{formatDateTime(a.changed_at)}</div>
                    <div className="text-[11px] text-gray-400">by {a.changed_by ?? '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Pill tone={AUDIT_ACTION_TONES[a.action]}>{a.action}</Pill>
                    <div className="font-mono text-[11px] text-gray-700 mt-1 break-all">{a.table_name}</div>
                    <div className="font-mono text-[11px] text-gray-400 break-all">{a.record_key}</div>
                  </td>
                  <td className="px-4 py-3">
                    <pre className="text-[10px] bg-red-50/60 text-red-900 rounded-lg p-2 overflow-x-auto max-h-48 whitespace-pre-wrap break-all">{prettyJson(a.old_value)}</pre>
                  </td>
                  <td className="px-4 py-3">
                    <pre className="text-[10px] bg-emerald-50/60 text-emerald-900 rounded-lg p-2 overflow-x-auto max-h-48 whitespace-pre-wrap break-all">{prettyJson(a.new_value)}</pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );

  /* ── tools ──────────────────────────────────────────────────────────── */

  const [grantForm, setGrantForm] = useState({ builderId: '', featureCode: '', technologySlug: '', units: '', reason: '' });
  const [granting, setGranting] = useState(false);
  const [syncSlug, setSyncSlug] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [grantResult, setGrantResult] = useState(null);

  const grantFeature = features.find((f) => f.code === grantForm.featureCode);
  const grantNeedsTechnology = grantFeature
    ? grantFeature.paid_credit_scope === 'TECHNOLOGY' || grantFeature.quota_scope === 'TECHNOLOGY'
    : false;

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!grantForm.builderId.trim() || !grantForm.featureCode || grantForm.units === '') {
      toast.error('Builder id, feature and units are required.');
      return;
    }
    if (grantNeedsTechnology && !grantForm.technologySlug) {
      toast.error(`${featureName(grantForm.featureCode)} credits are scoped per technology — pick one.`);
      return;
    }
    setGranting(true);
    try {
      const row = await billingApi.admin.grant(userId, {
        builderId: grantForm.builderId.trim(),
        featureCode: grantForm.featureCode,
        technologySlug: grantNeedsTechnology ? grantForm.technologySlug : null,
        units: Number(grantForm.units),
        reason: grantForm.reason.trim() || null,
      });
      setGrantResult(row);
      toast.success(`Granted ${grantForm.units} ${grantFeature?.unit_label_plural || 'units'} to builder ${grantForm.builderId.trim()}`);
      setGrantForm({ builderId: '', featureCode: '', technologySlug: '', units: '', reason: '' });
    } catch (err) {
      toast.error(`Grant: ${errMessage(err)}`);
    } finally {
      setGranting(false);
    }
  };

  const handleSync = async (e) => {
    e.preventDefault();
    if (!syncSlug) { toast.error('Pick a technology to resync.'); return; }
    setSyncing(true);
    try {
      const res = await billingApi.admin.syncElitePages(userId, syncSlug);
      setSyncResult(res);
      toast.success(`Resynced ${res?.technology || syncSlug}: ${res?.pages ?? 0} pages in the catalog`);
    } catch (err) {
      toast.error(`Resync: ${errMessage(err)}`);
    } finally {
      setSyncing(false);
    }
  };

  const renderTools = () => (
    <>
      <SectionHeader
        title="Tools"
        subtitle="One-off operations. Both are audited."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="text-sm font-black text-gray-900 flex items-center gap-2 mb-1"><Gift size={16} className="text-[#843FEF]" /> Grant entitlement</h3>
          <p className="text-xs text-gray-500 mb-4">
            Give a builder paid credits without a payment — support fixes, goodwill, pilots. The units land in the same
            balance a purchase would create.
          </p>
          <form onSubmit={handleGrant} className="space-y-3">
            <Field label="Builder id" hint="The numeric id of the Nexus builder account.">
              <TextInput value={grantForm.builderId} onChange={(x) => setGrantForm((f) => ({ ...f, builderId: x }))} placeholder="e.g. 42" />
            </Field>
            <Field label="Feature">
              <SelectInput
                value={grantForm.featureCode}
                onChange={(x) => setGrantForm((f) => ({ ...f, featureCode: x || '', technologySlug: '' }))}
                options={features.map((f) => ({ value: f.code, label: `${f.name} (${f.code})` }))}
                placeholder="Select a feature"
              />
            </Field>
            {grantNeedsTechnology && (
              <Field label="Technology" hint={`${featureName(grantForm.featureCode)} credits are usable only on the technology they were granted for.`}>
                <SelectInput
                  value={grantForm.technologySlug}
                  onChange={(x) => setGrantForm((f) => ({ ...f, technologySlug: x || '' }))}
                  options={technologyOptions}
                  placeholder="Select a technology"
                />
              </Field>
            )}
            <Field label="Units" hint={grantFeature ? `Number of ${grantFeature.unit_label_plural || 'units'} to add.` : 'Number of units to add.'}>
              <NumberInput min={1} value={grantForm.units} onChange={(x) => setGrantForm((f) => ({ ...f, units: x }))} />
            </Field>
            <Field label="Reason" hint="Recorded in the audit log.">
              <TextInput value={grantForm.reason} onChange={(x) => setGrantForm((f) => ({ ...f, reason: x }))} placeholder="e.g. support credit for failed generation" />
            </Field>
            <button
              type="submit"
              disabled={granting}
              className="w-full py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
            >
              {granting ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />} Grant entitlement
            </button>
          </form>
          {grantResult && (
            <pre className="mt-4 text-[10px] bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all text-gray-600">{prettyJson(grantResult)}</pre>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-black text-gray-900 flex items-center gap-2 mb-1"><RefreshCcw size={16} className="text-[#843FEF]" /> Resync Elite page catalog</h3>
          <p className="text-xs text-gray-500 mb-4">
            Re-reads a technology's published content and rebuilds the page index the Elite paywall counts against.
            Run this after publishing new Elite content, otherwise new pages are invisible to billing.
          </p>
          <form onSubmit={handleSync} className="space-y-3">
            <Field label="Technology">
              <SelectInput value={syncSlug} onChange={(x) => setSyncSlug(x || '')} options={technologyOptions} placeholder="Select a technology" />
            </Field>
            <button
              type="submit"
              disabled={syncing}
              className="w-full py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
            >
              {syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCcw size={15} />} Resync page catalog
            </button>
          </form>
          {syncResult && (
            <div className="mt-4 p-3 rounded-lg bg-emerald-50 text-emerald-800 text-xs">
              <b>{syncResult.technology}</b> — {syncResult.pages} page(s) indexed.
            </div>
          )}
        </Card>
      </div>
    </>
  );

  /* ── shell ──────────────────────────────────────────────────────────── */

  const dirtyCount = Object.keys(drafts).length;

  return (
    <div className="min-h-screen bg-[#F6F7FB] p-4 sm:p-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-[#843FEF] flex items-center justify-center">
                <CreditCard size={18} className="text-white" />
              </span>
              Nexus Billing
            </h1>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              Feature-based billing for TSG Nexus — free quotas, what builders can buy, what it costs, and what
              they actually bought. Everything here is read from the billing database and written straight back to
              it; the server validates and versions every change.
            </p>
            {config?.settings?.['billing.config_version'] !== undefined && (
              <p className="text-[11px] text-gray-400 mt-1">
                Config version <span className="font-mono">{String(config.settings['billing.config_version'])}</span>
                {dirtyCount > 0 && <span className="text-amber-600 font-semibold"> · {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}</span>}
              </p>
            )}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-bold">Could not load billing configuration.</div>
              <div className="text-xs mt-0.5">{error}</div>
              <div className="text-xs mt-1 text-red-600/80">This screen requires an admin account (users.role = 'admin').</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-5 overflow-x-auto">
          <div className="flex gap-1 min-w-max bg-white rounded-2xl border border-gray-100 shadow-sm p-1.5">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold whitespace-nowrap flex items-center gap-2 transition-colors ${
                    active ? 'bg-purple-50 text-[#843FEF]' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <Card className="p-16 flex flex-col items-center justify-center gap-3">
            <Loader2 size={26} className="animate-spin text-[#843FEF]" />
            <p className="text-sm text-gray-400">Loading billing configuration…</p>
          </Card>
        ) : !config ? (
          <Card><Empty>No configuration available.</Empty></Card>
        ) : (
          <>
            {tab === 'features' && renderFeatures()}
            {tab === 'products' && renderProducts()}
            {tab === 'tiers' && renderTiers()}
            {tab === 'tax' && renderTax()}
            {tab === 'settings' && renderSettings()}
            {tab === 'orders' && renderOrders()}
            {tab === 'audit' && renderAudit()}
            {tab === 'tools' && renderTools()}
          </>
        )}
      </div>

      <ToastStack toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  );
};

/** Percent text box that stores basis points (1800 bps = 18%). */
function PercentInput({ bps, onChangeBps, disabled }) {
  const toText = (b) => (num(b) === null ? '' : String(num(b) / 100));
  const [text, setText] = useState(toText(bps));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(toText(bps)); }, [bps, focused]);
  return (
    <div>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          disabled={disabled}
          value={text}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); setText(toText(bps)); }}
          onChange={(e) => {
            setText(e.target.value);
            const t = e.target.value.trim();
            if (t === '') { onChangeBps(null); return; }
            const n = Number(t);
            onChangeBps(Number.isFinite(n) ? Math.round(n * 100) : null);
          }}
          className={`${INPUT} pr-6`}
        />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
      </div>
      <p className="text-[10px] text-gray-400 mt-0.5">{num(bps) === null ? 'not set' : `${num(bps)} bps stored`}</p>
    </div>
  );
}

export default NexusBillingAdmin;
